// imported from myintelli-onnx commit 9f78984 on 2026-05-04
// do not edit manually — regenerate via `node scripts/sync-onnx-vendor.mjs`
/**
 * Face alignment via similarity transform (rotation + scale + translation).
 *
 * Used to warp the raw frame to a 112×112 ArcFace-normalised crop before
 * embedding. Implements the same transform as Python's cv2.estimateAffinePartial2D
 * + cv2.warpAffine but in pure JS so it runs identically on web, RN, and Node.
 */

import type { ImageFrame, Landmarks5 } from './types.js';

/** 2×3 affine matrix stored flat: [m00, m01, m02, m10, m11, m12]. */
export type AffineMatrix = readonly [number, number, number, number, number, number];

/**
 * Estimate a similarity transform (4 DOF: scale, rotation, tx, ty) from
 * 5 source landmarks to a 5-point destination template.
 *
 * Analytical least-squares solution (Umeyama):
 *   x' = a·x − b·y + tx
 *   y' = b·x + a·y + ty
 *
 * Returns a 2×3 matrix [[a,-b,tx],[b,a,ty]] stored flat.
 */
export function estimateSimilarityTransform(
  src: Landmarks5,
  dst: ReadonlyArray<readonly [number, number]>,
): AffineMatrix {
  const n = src.length; // 5
  let mxs = 0, mys = 0, mxd = 0, myd = 0;
  for (let i = 0; i < n; i++) {
    mxs += src[i]![0]; mys += src[i]![1];
    mxd += dst[i]![0]; myd += dst[i]![1];
  }
  mxs /= n; mys /= n; mxd /= n; myd /= n;

  let numA = 0, numB = 0, denom = 0;
  for (let i = 0; i < n; i++) {
    const xcs = src[i]![0] - mxs, ycs = src[i]![1] - mys;
    const xcd = dst[i]![0] - mxd, ycd = dst[i]![1] - myd;
    numA += xcs * xcd + ycs * ycd;
    numB += xcs * ycd - ycs * xcd;
    denom += xcs * xcs + ycs * ycs;
  }

  const a = denom === 0 ? 1 : numA / denom;
  const b = denom === 0 ? 0 : numB / denom;
  const tx = mxd - a * mxs + b * mys;
  const ty = myd - b * mxs - a * mys;

  // 2×3: row0 = [a, -b, tx], row1 = [b, a, ty]
  return [a, -b, tx, b, a, ty];
}

/**
 * Apply a 2×3 affine warp to `src`, producing a `dstW×dstH` RGBA ImageFrame.
 * Uses inverse mapping with nearest-neighbour sampling.
 */
export function warpAffine(
  src: ImageFrame,
  M: AffineMatrix,
  dstW: number,
  dstH: number,
): ImageFrame {
  const [m00, m01, m02, m10, m11, m12] = M;
  // Invert the 2×2 rotation sub-matrix
  const det = m00 * m11 - m01 * m10;
  if (Math.abs(det) < 1e-10) {
    return { data: new Uint8ClampedArray(dstW * dstH * 4), width: dstW, height: dstH, format: 'rgba' };
  }
  const inv00 = m11 / det, inv01 = -m01 / det;
  const inv10 = -m10 / det, inv11 = m00 / det;
  // Inverse translation: inv_t = -inv_R * t
  const inv02 = -(inv00 * m02 + inv01 * m12);
  const inv12 = -(inv10 * m02 + inv11 * m12);

  const srcStride = src.format === 'rgba' ? 4 : 3;
  const out = new Uint8ClampedArray(dstW * dstH * 4);

  for (let yo = 0; yo < dstH; yo++) {
    for (let xo = 0; xo < dstW; xo++) {
      const xs = Math.round(inv00 * xo + inv01 * yo + inv02);
      const ys = Math.round(inv10 * xo + inv11 * yo + inv12);
      const outBase = (yo * dstW + xo) * 4;

      if (xs < 0 || ys < 0 || xs >= src.width || ys >= src.height) {
        out[outBase + 3] = 255;
        continue;
      }

      const srcBase = (ys * src.width + xs) * srcStride;
      if (src.format === 'bgr') {
        out[outBase]     = src.data[srcBase + 2] ?? 0;
        out[outBase + 1] = src.data[srcBase + 1] ?? 0;
        out[outBase + 2] = src.data[srcBase]     ?? 0;
      } else {
        out[outBase]     = src.data[srcBase]     ?? 0;
        out[outBase + 1] = src.data[srcBase + 1] ?? 0;
        out[outBase + 2] = src.data[srcBase + 2] ?? 0;
      }
      out[outBase + 3] = 255;
    }
  }

  return { data: out, width: dstW, height: dstH, format: 'rgba' };
}

/**
 * Crop a rectangular region from `src` (clamped to bounds) and resize to
 * `dstW×dstH` using nearest-neighbour. Used for anti-spoof face crop preparation.
 */
export function cropAndResize(
  src: ImageFrame,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  dstW: number,
  dstH: number,
): ImageFrame {
  const cropW = x2 - x1;
  const cropH = y2 - y1;
  const srcStride = src.format === 'rgba' ? 4 : 3;
  const out = new Uint8ClampedArray(dstW * dstH * 4);

  for (let yo = 0; yo < dstH; yo++) {
    for (let xo = 0; xo < dstW; xo++) {
      const xs = Math.round(x1 + (xo + 0.5) * cropW / dstW - 0.5);
      const ys = Math.round(y1 + (yo + 0.5) * cropH / dstH - 0.5);
      const outBase = (yo * dstW + xo) * 4;

      if (xs < 0 || ys < 0 || xs >= src.width || ys >= src.height) {
        out[outBase + 3] = 255;
        continue;
      }

      const srcBase = (ys * src.width + xs) * srcStride;
      if (src.format === 'bgr') {
        out[outBase]     = src.data[srcBase + 2] ?? 0;
        out[outBase + 1] = src.data[srcBase + 1] ?? 0;
        out[outBase + 2] = src.data[srcBase]     ?? 0;
      } else {
        out[outBase]     = src.data[srcBase]     ?? 0;
        out[outBase + 1] = src.data[srcBase + 1] ?? 0;
        out[outBase + 2] = src.data[srcBase + 2] ?? 0;
      }
      out[outBase + 3] = 255;
    }
  }

  return { data: out, width: dstW, height: dstH, format: 'rgba' };
}
